import React from 'react';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import { color } from 'styled-system';
import isUrl from 'is-url';

import { Box, Flex, Text, SvgIcon, ButtonFilled, Formik, Link, Grid } from 'Components/Base';
import { FormTextArea, FormTextInput } from 'Components/Base/Form/FormikContainer/FormikInput';
import useFileUpload from 'Routes/Classroom/Domain/useFileUpload';
import { get } from 'Components/Base/constants';
import ButtonOutline from 'Components/Base/Button/ButtonOutlined';
import { localeDate } from 'Util/datetime';
const { FormContainer, Form } = Formik;

const STATUS_NAME = {
  SUBMITTED: 'SUBMITTED',
  CANCELED: 'CANCELED',
  GRADED: 'GRADED',
  PASSED: 'PASSED',
  FAILED: 'FAILED',
};

const ASSIGNMENT_TYPE = {
  URL: 'URL',
  FILE: 'FILE',
};

const POSSIBLE_FILE_TYPES = {
  ZIP: 'ZIP',
  DOCX: 'DOCX',
  DOC: 'DOC',
  XLS: 'XLS',
  XLSX: 'XLSX',
  PDF: 'PDF',
};

const ACCEPT_FILE_TYPE = {
  ZIP: 'application/zip',
  DOC: 'application/msword',
  DOCX: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  XLS: 'application/vnd.ms-excel',
  XLSX: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  PDF: 'application/pdf',
};

const FormLayout = styled(Grid)`
  grid-template-columns: minmax(0, 1fr);
  @media screen and (min-width: ${(props) => props.theme.breakpoints[2]}) {
    grid-template-columns: minmax(0, 120px) minmax(0, 1fr);
  }
  @media screen and (min-width: 1440px) {
    grid-template-columns: minmax(0, 180px) minmax(0, 1fr);
  }
`;

const AssignmentCardWrapper = styled((props) => <Box bg={'white'} {...props} />)`
  box-shadow: ${get('shadows.primary')};
  border-radius: 4px;
  border: 2px solid;
  border-color: ${({ theme }) => theme?.colors?.greyscale?.grey5 ?? '#f2f2f2'};
  width: 100%;
`;

const StatusIndicator = styled.span`
  ${color}
  margin-right: 12px;
  height: 12px;
  width: 12px;
  border-radius: 50%;
  display: inline-block;
`;

const TextEllipsis = styled((props) => <Text {...props} />)`
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;
  border-radius: 8px;
  flex-grow: 1;
  border: ${(props) => (props.isError ? '1px solid red' : '')};
`;

const AssignmentCard = ({ type, name, fileDownLoadButton }) => {
  return (
    <AssignmentCardWrapper>
      <Flex flexDirection="row" justifyContent={'space-between'} alignItems={'center'}>
        <Flex flexDirection={'row'} alignItems={'center'} p={2} overflow="hidden">
          <SvgIcon icon={SvgIcon.ICON_LIST.ASSIGNMENT_NOTE} size={24} color={'primary'} mr={2} />
          {type && type === 'FILE' ? (
            <TextEllipsis>{name}</TextEllipsis>
          ) : (
            <TextEllipsis>
              <Link as="a" href={name} target="_blank">
                {name}
              </Link>
            </TextEllipsis>
          )}
        </Flex>
        {type === 'FILE' ? fileDownLoadButton ?? <></> : null}
      </Flex>
    </AssignmentCardWrapper>
  );
};

const UploadField = ({ onFileChange, filePolicy, attachmentError, setErrors }) => {
  const [inputRef, { files, clearFile, uploadFile }] = useFileUpload({
    maximumSize: filePolicy.fileSizeLimit ? filePolicy.fileSizeLimit * 1024 * 1024 : 26214400, //25MB,
    override: true,
    onFileSizeExceedLimit: () => {
      setErrors(
        'attachment',
        `ขนาดไฟล์เกิน ${filePolicy.fileSizeLimit ?? '25'} MB กรุณาลองใหม่อีกครั้ง`
      );
    },
  });

  React.useEffect(() => {
    onFileChange('attachment', files);
  }, [files]);

  const possibleFileTypes = filePolicy?.fileTypes?.filter(
    (accept) => !!POSSIBLE_FILE_TYPES[accept]
  );

  return (
    <>
      <Flex flexDirection={'row'} alignItems={'center'}>
        <input
          ref={inputRef}
          type={'file'}
          hidden
          accept={Object.values(possibleFileTypes)
            .map((accept) => ACCEPT_FILE_TYPE[accept])
            .join(',')}
        />
        <TextEllipsis
          p={2}
          pl={4}
          mr={4}
          backgroundColor="greyscale.grey5"
          color={files ? 'black' : 'greyscale.grey3'}
          isError={Boolean(attachmentError)}
        >
          {files?.name ?? 'ยังไม่เลือกไฟล์'}
        </TextEllipsis>

        {files ? (
          <ButtonOutline
            type="button"
            p={2}
            onClick={clearFile}
            color={'primary'}
            textColor={'text'}
            borderRadius={4}
            borderWidth={2}
          >
            ยกเลิก
          </ButtonOutline>
        ) : (
          <ButtonOutline
            type="button"
            p={2}
            onClick={uploadFile}
            color={'primary'}
            textColor={'text'}
            borderRadius={4}
            borderWidth={2}
          >
            เลือก
          </ButtonOutline>
        )}
      </Flex>
      {attachmentError && (
        <>
          <Flex />
          <Text textStyle="body2" color="danger">
            {attachmentError}
          </Text>
        </>
      )}
    </>
  );
};

const StatusStateWrapper = ({
  status,
  submittedDate,
  reviewedDate,
  fullScore,
  score,
  ...props
}) => {
  let colorCode;
  let statusText;

  switch (status) {
    case STATUS_NAME.SUBMITTED: {
      colorCode = 'warning';
      statusText = 'อยู่ในระหว่างการตรวจ';
      break;
    }
    case STATUS_NAME.PASSED: {
      colorCode = 'success';
      statusText = 'ผ่าน';
      break;
    }
    case STATUS_NAME.FAILED: {
      colorCode = 'danger';
      statusText = 'ไม่ผ่าน';
      break;
    }
    default: {
      colorCode = 'disabled';
      statusText = 'ยังไม่ได้ส่ง';
      break;
    }
  }

  return (
    <>
      <Flex flexDirection={'row'} alignItems="center" {...props}>
        {/* TODO: Result status should check with grading type. For now we expects score = null is not graded or PASS_FAIL method. */}
        {typeof score !== 'number' ? (
          <>
            <StatusIndicator backgroundColor={colorCode} />
            <Text>{statusText}</Text>
          </>
        ) : (
          <Text>{`${score}${fullScore ? ` / ${fullScore} คะแนน` : ''}`}</Text>
        )}
      </Flex>
      {status === STATUS_NAME.SUBMITTED && (
        <>
          <Flex />
          <Text color="greyscale.grey3" width="auto" {...props}>{`${'ส่งเมื่อ : '} ${localeDate(
            submittedDate,
            'TH',
            'D MMM BBBB HH:mm น.'
          )}`}</Text>
        </>
      )}
    </>
  );
};

const FieldWrapper = ({ icon, children, ...props }) => {
  return (
    <Flex flexDirection={'row'} {...props}>
      <SvgIcon icon={icon} size={24} color={'primary'} />
      <Text pl={4}>{children}</Text>
    </Flex>
  );
};

function validateUrl(value) {
  let error;

  if (!value) error = 'กรุณาแนบ URL';

  if (!isUrl(value)) {
    error = 'กรุณาระบุ URL ที่ถูกต้อง';
  }

  return error;
}

const AssignmentForm = ({
  status,
  type,
  url,
  attachment,
  note,
  dueDate,
  onSubmit,
  submittedDate,
  reviewedDate,
  fileDownLoadButton,
  filePolicy,
  fullScore,
  score,
}) => {
  let icon;
  let label;

  switch (type) {
    case ASSIGNMENT_TYPE.FILE:
      icon = SvgIcon.ICON_LIST.ATTACHMENT;
      label = 'ไฟล์แนบ';
      break;

    default:
      icon = SvgIcon.ICON_LIST.LINK;
      label = ASSIGNMENT_TYPE.URL;
      break;
  }

  return (
    <FormContainer
      validate={(formValues) => {
        const error = {};

        if (formValues?.type === ASSIGNMENT_TYPE.URL) {
          const urlError = validateUrl(formValues.url);
          if (urlError) {
            error.url = urlError;
          }
        }

        if (formValues?.type === ASSIGNMENT_TYPE.FILE) {
          const hasAttachment = Boolean(
            formValues?.attachment?.name && formValues?.attachment?.file?.type
          );
          if (!hasAttachment) {
            error.attachment = 'กรุณาเลือกไฟล์';
          }
        }
        return error;
      }}
      onSubmit={onSubmit}
      initialValues={{ url: url, attachment: attachment, note: note, type: type, status: status }}
      enableReinitialize
    >
      {({
        setFieldValue,
        values: { status, type },
        errors,
        submitCount,
        resetForm,
        setFieldError,
        ...props
      }) => {
        const readOnly = Object.values(STATUS_NAME).includes(status);
        return (
          <Form>
            <FormLayout gridGap={{ _: 2, lg: 4 }}>
              {dueDate && (
                <>
                  <FieldWrapper icon={SvgIcon.ICON_LIST.CALENDAR_OUTLINED}>กำหนดส่ง</FieldWrapper>
                  <Flex ml={{ _: '40px', lg: 0 }}>
                    <Text color={new Date().getTime() > dueDate.getTime() ? 'danger' : ''}>
                      {`${localeDate(dueDate, 'TH', 'D MMM BBBB HH:mm น.')} `}
                    </Text>
                  </Flex>
                </>
              )}

              <FieldWrapper icon={icon} alignItems={'center'} mt={{ _: 4, lg: 0 }}>
                {label}
              </FieldWrapper>
              {type === ASSIGNMENT_TYPE.URL && (
                <>
                  {readOnly ? (
                    <AssignmentCard type={type} name={url} />
                  ) : (
                    <FormTextInput
                      name={'url'}
                      placeholder="ตัวอย่าง https://drive.google.com/file/d/....../view?usp=sharing"
                    />
                  )}
                </>
              )}
              {type === ASSIGNMENT_TYPE.FILE && (
                <>
                  {readOnly ? (
                    <AssignmentCard
                      type={type}
                      name={attachment}
                      fileDownLoadButton={fileDownLoadButton}
                    />
                  ) : (
                    <UploadField
                      onFileChange={setFieldValue}
                      filePolicy={filePolicy}
                      attachmentError={
                        (submitCount && errors?.attachment) ||
                        errors?.attachment !== 'กรุณาเลือกไฟล์'
                          ? errors?.attachment
                          : ''
                      }
                      setErrors={setFieldError}
                    />
                  )}
                </>
              )}
              <FieldWrapper icon={SvgIcon.ICON_LIST.ASSIGNMENT_STATUS} mt={{ _: 4, lg: 0 }}>
                ผลการตรวจ
              </FieldWrapper>
              <StatusStateWrapper
                status={status}
                submittedDate={submittedDate}
                reviewedDate={reviewedDate}
                score={score}
                fullScore={fullScore}
                ml={{ _: '40px', lg: 0 }}
              />
              {(note || !status) && (
                <FieldWrapper icon={SvgIcon.ICON_LIST.COMMENT} mt={{ _: 4, lg: 2 }}>
                  โน้ต
                </FieldWrapper>
              )}
              {status ? (
                <Text mt={2} ml={{ _: '40px', lg: 0 }}>
                  {note}
                </Text>
              ) : (
                <FormTextArea name={'note'} placeholder={'อธิบายเพิ่มเติม (ถ้ามี)'} />
              )}
              {(note || !status) && <Box />}
              {!status && (
                <ButtonFilled
                  borderRadius="20px"
                  height="40px"
                  py={2}
                  type="submit"
                  width={1}
                  maxWidth={{ _: 'auto', lg: '165px' }}
                >
                  ส่ง
                </ButtonFilled>
              )}
              {status === STATUS_NAME.SUBMITTED && (
                <ButtonOutline
                  borderRadius="20px"
                  height="40px"
                  py={2}
                  type="button"
                  onClick={() => {
                    resetForm({
                      values: {
                        url: null,
                        attachment: null,
                        note: null,
                        status: null,
                        type: type,
                      },
                    });
                  }}
                  width={1}
                  maxWidth={{ _: 'auto', lg: '165px' }}
                >
                  ส่งอีกครั้ง
                </ButtonOutline>
              )}
            </FormLayout>
          </Form>
        );
      }}
    </FormContainer>
  );
};

AssignmentForm.propTypes = {
  status: PropTypes.string,
  type: PropTypes.string,
  url: PropTypes.string,
  attachment: PropTypes.string,
  note: PropTypes.string,
  dueDate: PropTypes.instanceOf(Date),
  onSubmit: PropTypes.func,
  submittedDate: PropTypes.instanceOf(Date),
  reviewedDate: PropTypes.instanceOf(Date),
  signedUrl: PropTypes.string,
  fileDownLoadButton: PropTypes.node,
};

FieldWrapper.propTypes = {
  icon: PropTypes.func,
};

StatusStateWrapper.propTypes = {
  status: PropTypes.string,
  submittedDate: PropTypes.instanceOf(Date),
  reviewedDate: PropTypes.instanceOf(Date),
};

UploadField.propTypes = {
  onFileChange: PropTypes.func,
};

AssignmentCard.propTypes = {
  type: PropTypes.string,
  name: PropTypes.string,
  signedUrl: PropTypes.string,
  onDownloadFile: PropTypes.func,
};

export default AssignmentForm;
